import { DateTime } from "luxon"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"

import FullPageLoading from "@/components/fullpage_loading"
import { useLocale } from "@/contexts/locale"
import { PublicReservationStatus } from "@/graphql/graphql"
import { usePublicReservation } from "@/graphql/hooks"
import NotFound from "@/pages/not_found"
import { timeRange } from "@/shared/datetime"

import ReservationPageContent from "./page_content"
import ReservationPageOverlays from "./page_overlays"
import { useCancellationFlow } from "./use_cancellation_flow"
import { useConfirmationFlow } from "./use_confirmation_flow"
import { useModifyReservationFlow } from "./use_modify_reservation_flow"

const useCanModify = (
  reservation: ReturnType<typeof usePublicReservation>["reservation"],
  timezone: string | undefined,
) => {
  return useMemo(() => {
    if (!reservation || reservation.status !== PublicReservationStatus.Confirmed) return false
    if (!timezone) return false

    const now = DateTime.now().setZone(timezone)
    const startTime = DateTime.fromISO(reservation.startTime).setZone(timezone)
    const cancellationDeadline = reservation.venue?.reservationConfiguration?.cancellationDeadline

    if (cancellationDeadline) {
      return startTime.minus({ seconds: cancellationDeadline }) > now
    }

    return startTime > now
  }, [reservation, timezone])
}

const useSyncReservationLocale = (
  locale: string,
  reservationLocale: string | null | undefined,
  setLocale: (locale: string) => void,
) => {
  useEffect(() => {
    if (!reservationLocale || reservationLocale === locale) return

    setLocale(reservationLocale)
  }, [locale, reservationLocale, setLocale])
}

const readStoredToken = (key: string) => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStoredToken = (key: string, token: string) => {
  try {
    sessionStorage.setItem(key, token)
  } catch {
    // The ref fallback keeps the token available for this page load.
  }
}

const PublicReservationPage: React.FC = () => {
  const { reservationId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const confirmToken = searchParams.get("confirm_token")
  const [pendingConfirmToken, setPendingConfirmToken] = useState(confirmToken)
  const tokenRef = useRef<{ reservationId?: string; token: string | null }>({ reservationId, token: confirmToken })

  const storedTokenKey = `reservation_token_${reservationId}`
  useEffect(() => {
    if (confirmToken && reservationId) {
      tokenRef.current = { reservationId, token: confirmToken }
      writeStoredToken(storedTokenKey, confirmToken)
      setPendingConfirmToken(confirmToken)
      setSearchParams({}, { replace: true })
    }
  }, [confirmToken, reservationId, storedTokenKey, setSearchParams])

  const memoryToken = tokenRef.current.reservationId === reservationId ? tokenRef.current.token : null
  const reservationToken = confirmToken || memoryToken || readStoredToken(storedTokenKey) || ""

  const { reservation, loading, refetch } = usePublicReservation({ uuid: reservationId })
  const { interstitialMode, setInterstitialMode } = useConfirmationFlow({
    reservationId,
    confirmToken: pendingConfirmToken,
    reservation,
    refetch,
  })

  const { locale, setLocale } = useLocale()

  const venue = reservation?.venue
  const timezone = reservation?.timezone || venue?.timezone
  const coverImage = venue?.images?.[0]?.url
  const canModify = useCanModify(reservation, timezone)

  const {
    cancelContactHref,
    cancelLoading,
    cancellationDeadlineLabel,
    cancelModalState,
    closeCancelModal,
    handleCancelClick,
    handleConfirmCancellation,
    showCancelAction,
  } = useCancellationFlow({
    refetch,
    reservation,
    reservationId,
    timezone,
    token: reservationToken,
  })

  const {
    bookingPolicySection,
    bookingPolicyTiles,
    fullVenue,
    handleModifyClick,
    handleModifyClose,
    handleModifyComplete,
    modifyInitialValues,
    modifyModalOpen,
    modifyValidationSchema,
    originalReservation,
    token: modifyToken,
    venueLoading,
    venueLogoUrl,
  } = useModifyReservationFlow({ reservation, reservationId, timezone, token: reservationToken })

  useSyncReservationLocale(locale, reservation?.locale, setLocale)

  const isLoadingState = loading || interstitialMode === "loading"

  if (isLoadingState) {
    return <FullPageLoading />
  }

  if (!reservation) {
    return <NotFound />
  }

  const formattedDate = DateTime.fromISO(reservation.startTime).setZone(timezone).toFormat("EEEE - MMM dd, yyyy")
  const formattedTimeRange = timeRange(
    DateTime.fromISO(reservation.startTime).setZone(timezone),
    DateTime.fromISO(reservation.endTime).setZone(timezone),
  )
  const timezoneAbbreviation = DateTime.fromISO(reservation.startTime).setZone(timezone).toFormat("ZZZZ")

  return (
    <>
      <ReservationPageContent
        bookingPolicySection={bookingPolicySection}
        bookingPolicyTiles={bookingPolicyTiles}
        canModify={canModify}
        cancellationDeadlineLabel={cancellationDeadlineLabel}
        coverImage={coverImage}
        customerNote={reservation.customerNote}
        formattedDate={formattedDate}
        formattedTimeRange={formattedTimeRange}
        fullVenue={fullVenue}
        modifyInitialValues={modifyInitialValues}
        modifyModalOpen={modifyModalOpen}
        modifyValidationSchema={modifyValidationSchema}
        onCancel={handleCancelClick}
        onModify={handleModifyClick}
        onModifyClose={handleModifyClose}
        onModifyComplete={handleModifyComplete}
        originalReservation={originalReservation}
        modifyToken={modifyToken}
        peopleAmount={reservation.peopleAmount}
        referenceNumber={reservation.referenceNumber}
        showCancelAction={showCancelAction}
        status={reservation.status}
        tableName={reservation.tableName}
        timezoneAbbreviation={timezoneAbbreviation}
        venue={venue}
        venueLoading={venueLoading}
        venueLogoUrl={venueLogoUrl}
        venueName={venue?.name || reservation.venueName}
      />

      <ReservationPageOverlays
        cancelContactHref={cancelContactHref}
        cancelLoading={cancelLoading}
        cancelModalState={cancelModalState}
        closeCancelModal={closeCancelModal}
        handleConfirmCancellation={() => {
          void handleConfirmCancellation()
        }}
        interstitialMode={interstitialMode}
        reservationStatus={reservation.status}
        setInterstitialMode={setInterstitialMode}
        venueSlug={reservation.venue.slug}
      />
    </>
  )
}

export default PublicReservationPage
